import { Injectable } from "@angular/core";
import { Environment } from "@kno2/core/configuration";
import { ApplicationInsightsService } from "@kno2/core/integrations";
import { BehaviorSubject, combineLatest, Subject } from "rxjs";
import { filter, take } from "rxjs/operators";
import { io, Socket } from "socket.io-client";
import { AuthSliceFacade } from "../../+store";
import { AuthRequestResult, SyncReleaseTypesResult } from "../models/socket.model";
import { ProfileService } from "../profile/profile.service";

@Injectable({
    providedIn: "root"
})
export class SocketService {
    private client: Socket | null = null;

    // create an observable to track if the client is connected to the server
    private clientConnected = new BehaviorSubject<boolean>(false);
    private syncReleaseTypes = new Subject<SyncReleaseTypesResult>();

    public clientConnected$ = this.clientConnected.asObservable();
    public syncReleaseTypes$ = this.syncReleaseTypes.asObservable();

    constructor(
        private environment: Environment,
        private authSlice: AuthSliceFacade,
        private profileService: ProfileService,
        private appInsights: ApplicationInsightsService
    ) {}

    public start(): void {
        const { socketUrl } = this.environment;
        this.client = io(socketUrl, {
            forceNew: true,
            transports: ["websocket"]
        });

        this.client.on("connect_error", (err: any) => this.appInsights.trackException(err));

        this.client.on("connect", () => {
            this.handleConnected();
        });
        this.client.on("authRequestResult", (result: AuthRequestResult) => this.handleAuthRequestResult(result));
        this.client.on("SyncReleaseTypes", (payload: SyncReleaseTypesResult) => {
            this.syncReleaseTypes.next(payload);
        });
        this.client.on("AccountChangeRequest", () => window.location.reload());
    }

    public stop(): void {
        this.client?.disconnect();
        this.clientConnected.next(false);
        this.client = null;
        this.clientConnected.complete();
        this.syncReleaseTypes.complete();
    }

    private handleConnected() {
        combineLatest([this.authSlice.authToken$, this.profileService.getUserId$()])
            .pipe(
                filter(([token, userId]) => !!token && !!userId),
                take(1)
            )
            .subscribe({
                next: ([token, userId]) => {
                    this.client.emit("authRequest", {
                        apiUrl: this.environment.baseUrl,
                        authToken: token,
                        userId
                    });
                },
                error: (err) => {
                    console.error("Socket Service failed to auth:", err);
                }
            });
    }

    private handleAuthRequestResult(result: AuthRequestResult) {
        if (!result.success) {
            console.error("Could not successfully authenticate to delivery service: ", result);
            return this.stop();
        }

        this.clientConnected.next(true);
    }
}
